<template>
    <div class="verify-email">
        <UICard
            :header="{
                logo: 'user',
                title: $t('verifyEmail.title'),
            }"
            :footer="{
                title: '',
                label: $t('verifyEmail.backToLogin'),
                to: '/login',
            }"
        >
            <div class="verify-email__content">
                <UIInputCode
                    :label="$t('registration.emailVerification')"
                    :error="codeError"
                    :fieldOne="code.fieldOne"
                    :fieldTwo="code.fieldTwo"
                    :fieldThree="code.fieldThree"
                    :fieldFour="code.fieldFour"
                    :fieldFive="code.fieldFive"
                    :fieldSix="code.fieldSix"
                    @field="onChangeField"
                />

                <div class="verify-email__resend">
                    <span v-if="resendSuccess" class="verify-email__sent">{{ $t('verifyEmail.codeSent') }}</span>
                    <span v-else-if="resendCooldown > 0" class="verify-email__cooldown">{{ resendCooldown }}s</span>
                    <button
                        v-else
                        class="verify-email__resend-btn"
                        :disabled="resendLoading"
                        @click="onResend"
                    >{{ $t('login.sendVerifyCode') }}</button>
                </div>

                <UIButton
                    :label="$t('verifyEmail.confirm')"
                    view="main"
                    :full="true"
                    :disabled="getDisabled"
                    :uppercase="true"
                    @clicked="onConfirm"
                />
            </div>
        </UICard>
    </div>
</template>

<script>
import UICard from "@/components/ui/card/card.vue";
import UIInputCode from "@/components/ui/input/code.vue";
import UIButton from "@/components/ui/button/button.vue";

import Api from "@/api/api";
import {RESPONSE_STATUSES} from "@/utils/constants.js";
import {setCookie} from "@/utils/functions.js";

const api = new Api();
const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 60;

export default {
    name: "VerifyEmail",
    components: {
        UICard,
        UIInputCode,
        UIButton,
    },
    data() {
        return {
            email: "",
            code: {
                fieldOne: "",
                fieldTwo: "",
                fieldThree: "",
                fieldFour: "",
                fieldFive: "",
                fieldSix: "",
            },
            loading: false,
            codeError: false,
            resendLoading: false,
            resendSuccess: false,
            resendCooldown: 0,
            cooldownTimer: null,
        };
    },
    computed: {
        getCode() {
            return Object.values(this.code).join("");
        },
        getDisabled() {
            return this.loading || this.getCode.length !== CODE_LENGTH;
        },
    },
    mounted() {
        this.email = this.$route?.query?.email || "";
    },
    beforeUnmount() {
        if (this.cooldownTimer) clearInterval(this.cooldownTimer);
    },
    methods: {
        onChangeField({key, value}) {
            this.code[key] = value;
        },

        async onResend() {
            if (!this.email || this.resendLoading) return;
            this.resendLoading = true;
            this.resendSuccess = false;
            try {
                await api.resendVerification(this.email);
                this.resendSuccess = true;
                this.resendCooldown = RESEND_COOLDOWN;
                this.cooldownTimer = setInterval(() => {
                    this.resendCooldown--;
                    if (this.resendCooldown <= 0) {
                        clearInterval(this.cooldownTimer);
                        this.resendSuccess = false;
                    }
                }, 1000);
            } catch (e) {
                // ignore
            } finally {
                this.resendLoading = false;
            }
        },

        async onConfirm() {
            try {
                this.loading = true;
                this.codeError = false;

                const {status} = await api.verifyByCode({
                    email: this.email,
                    code: this.getCode,
                });

                if (status === RESPONSE_STATUSES.success) {
                    setCookie("introduction", this.email, "365d");
                    this.$router.push("/");
                } else {
                    this.codeError = true;
                }
            } catch (e) {
                this.codeError = true;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss">
.verify-email {
    display: flex;
    justify-content: center;

    .verify-email__content {
        display: flex;
        flex-direction: column;
        grid-row-gap: 16px;
    }

    .verify-email__resend {
        display: flex;
        justify-content: center;
        min-height: 20px;
    }

    .verify-email__resend-btn {
        font-size: 14px;
        color: $main;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;

        &:disabled {
            opacity: 0.5;
            cursor: default;
        }
    }

    .verify-email__cooldown,
    .verify-email__sent {
        font-size: 14px;
        color: $txt-light;
    }
}
</style>
